import React, { useEffect, useState, useContext } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle, XCircle, Download, ChevronRight, Loader2, Building2, MapPin, FileText, User, Calendar, Hash, Package } from "lucide-react";
import { getInstalacion } from "../../../../../services";
import { formatoFechasEs } from "../../../../../functions/funciones";
import { descargarCertificadoKae } from "../../../../../functions/pdfs";
import { UsuarioContext } from "../../../../../providers/userContext";

const InfoRow = ({ icon: Icon, label, children }) => (
  <div className="flex items-start gap-3 py-3" style={{ borderBottom: "1px solid #f1f5f9" }}>
    {Icon && (
      <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-gray-100 text-gray-500 mt-0.5 shrink-0">
        <Icon size={16} strokeWidth={1.7} />
      </span>
    )}
    <div className="flex-1 min-w-0">
      <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</div>
      <div className="text-sm text-gray-900 mt-0.5 break-words">{children || "—"}</div>
    </div>
  </div>
);

const StatusBadge = ({ realizada, t }) =>
  realizada ? (
    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg text-xs font-medium bg-green-100 text-green-700">
      <CheckCircle className="w-3.5 h-3.5" />
      {t("status.completed", { defaultValue: "Realizada" })}
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg text-xs font-medium bg-orange-100 text-orange-600">
      <XCircle className="w-3.5 h-3.5" />
      {t("status.pending", { defaultValue: "Pendiente" })}
    </span>
  );

const InstalacionLegalia = () => {
  const { t } = useTranslation("common");
  const { id } = useParams();
  const user = useContext(UsuarioContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await getInstalacion(id);
      setData(response);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="animate-spin w-8 h-8 text-gray-400" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">{t("installations.messages.noInstallations")}</p>
      </div>
    );
  }

  const totalSenales = data.productos?.reduce((sum, p) => sum + (p.cantidad || 0), 0) || 0;
  const ubicacion = [data.propertie?.direccion, data.propertie?.codigoPostal, data.propertie?.municipio, data.propertie?.provincia]
    .filter(Boolean)
    .join(", ");
  const tecnicoNombre = data.technician ? `${data.technician.nombre} ${data.technician.apellidos}` : "";

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      <div className="max-w-8xl mx-auto p-3 sm:p-6">
        {/* Breadcrumb estilo Legalia */}
        <nav className="flex items-center text-sm text-gray-500 mb-4">
          <Link to="/instalaciones" className="hover:text-gray-900 hover:underline">
            {t("installations.title")}
          </Link>
          <ChevronRight size={14} className="mx-1.5 text-gray-300" />
          <span className="text-gray-900 font-medium truncate">
            {data.propertie?.razonSocial || t("installations.detail.title")}
          </span>
        </nav>

        {/* Header */}
        <div className="bg-white shadow-sm rounded-2xl p-4 sm:p-6 mb-4">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">
                {data.propertie?.razonSocial || t("installations.detail.title")}
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                {data.propertie?.nif || ""}{ubicacion && data.propertie?.nif ? " · " : ""}{ubicacion}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2 shrink-0">
              <StatusBadge realizada={data.realizada} t={t} />
              {data.realizada && (
                <button
                  onClick={() =>
                    descargarCertificadoKae({
                      tecnicoNombre,
                      ubicacionFinca: ubicacion,
                      lugar: data.propertie?.municipio || data.propertie?.provincia || "",
                      fecha: data.fecha_instalacion,
                      nifPropiedad: data.propertie?.nif,
                    })
                  }
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-white text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
                  style={{ border: "1px solid #e5e7eb" }}
                >
                  <Download size={14} />
                  {t("installations.detail.downloadCertificate")}
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Información general */}
          <div className="bg-white shadow-sm rounded-2xl p-4 sm:p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-3">
              {t("installations.detail.info")}
            </h2>
            <div>
              <InfoRow icon={Hash} label={t("installations.columns.nif")}>
                {data.propertie?.nif || "—"}
              </InfoRow>
              <InfoRow icon={Building2} label={t("installations.columns.community")}>
                {data.propertie?.razonSocial ? (
                  data.propertie?.nif ? (
                    <Link
                      to={
                        user.rol === "AAFF"
                          ? `/comunidades/${data.propertie.nif}`
                          : `/aaff/${data.aaff?.nif}/comunidades/${data.propertie.nif}`
                      }
                      className="font-medium text-gray-900 hover:underline"
                    >
                      {data.propertie.razonSocial}
                    </Link>
                  ) : (
                    data.propertie.razonSocial
                  )
                ) : (
                  "—"
                )}
              </InfoRow>
              <InfoRow icon={MapPin} label={t("installations.columns.address")}>
                {ubicacion || "—"}
              </InfoRow>
              <InfoRow icon={Calendar} label={t("installations.columns.invoiceDate")}>
                {data.invoiceDate ? formatoFechasEs(data.invoiceDate) : "—"}
              </InfoRow>
              <InfoRow icon={Calendar} label={t("installations.columns.invoiceDate2")}>
                {data.invoiceDate2 ? formatoFechasEs(data.invoiceDate2) : "—"}
              </InfoRow>
              <InfoRow icon={User} label={t("installations.columns.technician")}>
                {tecnicoNombre || "—"}
              </InfoRow>
              <InfoRow icon={Calendar} label={t("installations.columns.performedDate")}>
                {data.realizada && data.fecha_instalacion ? formatoFechasEs(data.fecha_instalacion) : "—"}
              </InfoRow>
              <InfoRow icon={Package} label={t("installations.columns.signals")}>
                <span className="font-semibold text-gray-900">{totalSenales}</span>
              </InfoRow>
              {data.observaciones && (
                <div className="mt-4 p-3 rounded-xl bg-gray-50">
                  <div className="flex items-center gap-2 mb-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    <FileText size={14} />
                    {t("installations.detail.observations")}
                  </div>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{data.observaciones}</p>
                </div>
              )}
            </div>
          </div>

          {/* Productos instalados */}
          <div className="bg-white shadow-sm rounded-2xl p-4 sm:p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-3">
              {t("installations.detail.products")}
            </h2>
            {data.productos && data.productos.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" style={{ tableLayout: "auto" }}>
                  <thead>
                    <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
                      <th className="text-left text-xs font-medium text-gray-500 pb-3 pr-4 whitespace-nowrap">
                        {t("installations.columns.product")}
                      </th>
                      <th className="text-left text-xs font-medium text-gray-500 pb-3 pr-4 whitespace-nowrap">
                        {t("installations.columns.warehouse")}
                      </th>
                      <th className="text-right text-xs font-medium text-gray-500 pb-3 whitespace-nowrap">
                        {t("installations.columns.quantity")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.productos.map((item) => (
                      <tr key={item.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                        <td className="py-3 pr-4">
                          <div className="font-medium text-gray-900">{item.producto?.nombre}</div>
                          <div className="text-xs text-gray-400 mt-0.5">{item.producto?.sku}</div>
                        </td>
                        <td className="py-3 pr-4 text-gray-600">{item.almacen?.nombre || "—"}</td>
                        <td className="py-3 text-right font-semibold text-gray-900">{item.cantidad}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Package size={32} className="text-gray-300 mb-2" />
                <p className="text-sm text-gray-500">{t("installations.detail.noProducts")}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstalacionLegalia;
